// customerFormat.js
// Shared display helpers for `customers/{cid}` docs, used by the Overview
// tab, the Customers table and the customer detail drawer. This is the ONE
// place that knows how to turn the raw doc shape returned by
// `adminDashboardF-fetchCustomers` (Firestore Timestamps arrive as
// `{_seconds, _nanoseconds}` - the callable returns `{...doc.data(), id}`
// verbatim) into copy-ready strings. Keep every consumer importing from
// here instead of re-deriving date/status/usage logic locally.

import { normalizeTenantModules } from './tenantModulesCatalog';

// Converts any of the date-ish shapes this codebase hands around into a
// millisecond epoch number, or null when nothing usable was found:
//   - {_seconds, _nanoseconds} (raw Admin SDK Timestamp JSON, e.g. from
//     adminDashboardF-fetchCustomers)
//   - {seconds, nanoseconds} (client SDK Timestamp JSON)
//   - an object with a toMillis() method (a live Firestore Timestamp
//     instance, client or admin)
//   - an ISO date string
//   - a number already in milliseconds
//   - a Date instance
export function toMillis(value) {
  if (value === null || value === undefined) {
    return null;
  }
  if (value instanceof Date) {
    const ms = value.getTime();
    return Number.isFinite(ms) ? ms : null;
  }
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null;
  }
  if (typeof value === 'string') {
    const ms = Date.parse(value);
    return Number.isFinite(ms) ? ms : null;
  }
  if (typeof value === 'object') {
    if (typeof value.toMillis === 'function') {
      const ms = value.toMillis();
      return Number.isFinite(ms) ? ms : null;
    }
    const seconds = value._seconds ?? value.seconds;
    if (typeof seconds === 'number') {
      const nanos = value._nanoseconds ?? value.nanoseconds ?? 0;
      const ms = seconds * 1000 + Math.round(nanos / 1e6);
      return Number.isFinite(ms) ? ms : null;
    }
  }
  return null;
}

// Human date for the portal's cards/rows/footers. '—' when unparseable.
export function formatDate(value) {
  const ms = toMillis(value);
  if (ms === null) {
    return '—';
  }
  return new Date(ms).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

// Formats customers/{cid}.usageStats.storageBytes (written nightly by
// computeUsageStats in adminDashboardF.js) as a human-readable size. `bytes`
// is only ever a concrete number once usageStats exists (the aggregator
// always writes 0, never omits the field) - callers still guard for a
// missing/non-numeric value themselves before calling this (see the
// "usageStats absent entirely" branches in the table/drawer), so this only
// has to handle the numeric-formatting part. Moved verbatim from
// customerUtils.jsx (round 4).
export function formatStorageSize(bytes) {
  if (!Number.isFinite(bytes) || bytes < 0) {
    return '—';
  }
  if (bytes < 1024) {
    return `${bytes} B`;
  }
  const kb = bytes / 1024;
  if (kb < 1024) {
    return `${kb.toFixed(kb < 10 ? 1 : 0)} KB`;
  }
  const mb = kb / 1024;
  if (mb < 1024) {
    return `${mb.toFixed(mb < 10 ? 1 : 0)} MB`;
  }
  const gb = mb / 1024;
  return `${gb.toFixed(2)} GB`;
}

// The label the portal shows for a customer. Mirrors the office app's own
// company-label chain (`companyName || businessName || name`, see
// employeesF getMyAccess) minus businessName, which the portal never reads -
// the server writes both `name` and `companyName` on create and on a name
// edit (see adminDashboardF-editCustomer), so this stays in lockstep with
// what the signed-in owner sees inside their own app.
export function displayName(customer) {
  // Always a string: CustomerTable sorts with localeCompare on this value.
  return (customer?.companyName || customer?.name || '').trim() || customer?.email || customer?.id || '';
}

// Sign-up status derived from `emailVerified`, which ONLY exists on
// customers created through (or that have completed) the 2026-09-06 invite
// flow. Absent means the doc predates that tracking entirely - it is NOT
// evidence either way, so it renders as 'unknown' rather than "Invited".
export function customerStatus(customer) {
  if (customer?.emailVerified === true) {
    return {
      key: 'active',
      label: 'Active',
      color: '#2ea183',
      hint: 'Signed up through the invitation email',
    };
  }
  if (customer?.emailVerified === false) {
    return {
      key: 'invited',
      label: 'Invited',
      color: '#e0a23c',
      hint: 'Invitation email sent - the link has not been used yet',
    };
  }
  return {
    key: 'unknown',
    label: '—',
    color: '#96a5b4',
    hint: 'Created before sign-up tracking (2026-09-06)',
  };
}

// Aggregates `usageStats` across a customer list for the Overview cards.
// Only customers carrying a `usageStats` object are counted (one live
// tenant has none yet, before its first nightly run) - `counted` says how
// many contributed, `total` is the full list length, so callers can render
// "n of m customers counted" instead of implying full coverage.
export function sumUsage(customers) {
  const list = Array.isArray(customers) ? customers : [];
  let employees = 0;
  let jobsites = 0;
  let gcWorkers = 0;
  let gcSubCompanies = 0;
  let storageBytes = 0;
  let storageObjects = 0;
  let counted = 0;
  let latestComputedAt = null;

  list.forEach((customer) => {
    const usage = customer?.usageStats;
    if (!usage || typeof usage !== 'object') {
      return;
    }
    counted += 1;
    employees += Number(usage.employees) || 0;
    jobsites += Number(usage.jobsites) || 0;
    gcWorkers += Number(usage.gcWorkers) || 0;
    gcSubCompanies += Number(usage.gcSubCompanies) || 0;
    storageBytes += Number(usage.storageBytes) || 0;
    storageObjects += Number(usage.storageObjects) || 0;

    const computedMs = toMillis(usage.computedAt);
    if (computedMs !== null && (latestComputedAt === null || computedMs > latestComputedAt)) {
      latestComputedAt = computedMs;
    }
  });

  return {
    employees,
    jobsites,
    gcWorkers,
    gcSubCompanies,
    storageBytes,
    storageObjects,
    counted,
    total: list.length,
    latestComputedAt,
  };
}

// Add-on summary for the drawer's "Modules" card. `warehouse`/`gc` reflect
// today's two sellable add-ons (web.warehouse mirrors mobile.warehouse -
// they are LINKED_WEB_MOBILE_KEYS, always written together by
// CustomerModulesModal, so reading the web side is enough); `offCount`
// counts every explicit `false` across all three spaces so "n modules off"
// stays accurate even as the catalog grows. Absent `customer.modules`
// normalizes to fully-enabled (see normalizeTenantModules), so a customer
// with no modules map ever gets offCount 0.
export function addOnSummary(customer) {
  const normalized = normalizeTenantModules(customer?.modules);
  const offCount = ['web', 'mobile', 'restricted'].reduce((count, space) => {
    const offInSpace = Object.values(normalized[space]).filter((enabled) => enabled === false).length;
    return count + offInSpace;
  }, 0);

  return {
    warehouse: Boolean(normalized.web.warehouse),
    gc: Boolean(normalized.restricted.gc),
    offCount,
  };
}
